import type { GeocodeTier } from '@/features/geolocation/config';
import type {
  AddressInput,
  GeoBounds,
  GeocodeResult,
  LocationPrecision,
} from '@/features/geolocation/types/location';
import { nextTier } from '@/features/geolocation/utils/geocodeQuery';

export type PickerStatus = 'idle' | 'locating' | 'resolved' | 'notFound' | 'failed' | 'manual';

export interface PickerState {
  status: PickerStatus;
  /** Tiers applicable to the current attempt's address. */
  tiers: GeocodeTier[];
  /** Tier currently in flight. */
  tier: GeocodeTier | null;
  /**
   * Address snapshot the current attempt geocodes. Held here rather than in the
   * host form's live values, so a user who keeps typing cannot change the address
   * of a lookup that is already in flight.
   */
  attemptAddress: AddressInput | null;
  /** Tier that produced the hit — reported to the user. */
  resolvedTier: GeocodeTier | null;
  precision: LocationPrecision | null;
  displayName: string | null;
  /** Address key the current coordinates came from; null when manual or stored. */
  geocodedAddressKey: string | null;
  /** Blocks re-firing an auto-locate for unchanged input. */
  lastAttemptedAddressKey: string | null;
  /** Manual-override latch. While set, auto-locate is a no-op. */
  isPinned: boolean;
  bounds: GeoBounds | null;
  /** Bumped only when the map must recentre. */
  viewportNonce: number;
}

export type PickerEvent =
  | {
      type: 'AUTO_LOCATE_REQUESTED';
      addressKey: string;
      address: AddressInput;
      tiers: GeocodeTier[];
    }
  | {
      type: 'MANUAL_LOCATE_REQUESTED';
      addressKey: string;
      address: AddressInput;
      tiers: GeocodeTier[];
    }
  | { type: 'TIER_RESULT'; result: GeocodeResult; precision: LocationPrecision }
  | { type: 'TIER_MISS' }
  | { type: 'TIER_ERROR' }
  | { type: 'MAP_PICKED' }
  | { type: 'COORDS_EDITED' }
  | { type: 'CLEARED' };

/**
 * Stored coordinates are authoritative: a record that already has a position
 * starts pinned, so loading a form never triggers a geocode that could move it.
 */
export function initialPickerState(hasStoredCoordinates: boolean): PickerState {
  return {
    status: 'idle',
    tiers: [],
    tier: null,
    attemptAddress: null,
    resolvedTier: null,
    precision: null,
    displayName: null,
    geocodedAddressKey: null,
    lastAttemptedAddressKey: null,
    isPinned: hasStoredCoordinates,
    bounds: null,
    viewportNonce: 0,
  };
}

function startLocating(
  state: PickerState,
  event: { addressKey: string; address: AddressInput; tiers: GeocodeTier[] }
): PickerState {
  return {
    ...state,
    status: 'locating',
    tiers: event.tiers,
    tier: event.tiers[0],
    attemptAddress: event.address,
    resolvedTier: null,
    lastAttemptedAddressKey: event.addressKey,
    isPinned: false,
  };
}

/** A manual position never gets overwritten by geocoding; see MAP_PICKED. */
function pinToUserPosition(state: PickerState): PickerState {
  return {
    ...state,
    status: 'manual',
    tier: null,
    resolvedTier: null,
    precision: 'building',
    displayName: null,
    geocodedAddressKey: null,
    isPinned: true,
    bounds: null,
  };
}

export function locationPickerReducer(state: PickerState, event: PickerEvent): PickerState {
  switch (event.type) {
    case 'AUTO_LOCATE_REQUESTED': {
      // The manual-override latch is enforced here, in the reducer, rather than
      // by hiding UI — geocoding must not be able to overwrite a human decision.
      if (state.isPinned) return state;
      if (event.addressKey === state.lastAttemptedAddressKey) return state;
      if (event.tiers.length === 0) return state;

      return startLocating(state, event);
    }

    case 'MANUAL_LOCATE_REQUESTED': {
      // Explicitly asked for: un-pins and bypasses the unchanged-key guard so the
      // button always does something.
      if (event.tiers.length === 0) return state;

      return startLocating(state, event);
    }

    case 'TIER_RESULT': {
      return {
        ...state,
        status: 'resolved',
        resolvedTier: state.tier,
        tier: null,
        precision: event.precision,
        displayName: event.result.displayName,
        bounds: event.result.bounds,
        geocodedAddressKey: state.lastAttemptedAddressKey,
        viewportNonce: state.viewportNonce + 1,
      };
    }

    case 'TIER_MISS': {
      const following = state.tier ? nextTier(state.tiers, state.tier) : null;

      if (following) {
        return { ...state, status: 'locating', tier: following };
      }

      return { ...state, status: 'notFound', tier: null };
    }

    case 'TIER_ERROR': {
      return { ...state, status: 'failed', tier: null };
    }

    case 'MAP_PICKED': {
      // No viewport bump — never fight the user's own click.
      return pinToUserPosition(state);
    }

    case 'COORDS_EDITED': {
      // The map must pan to typed coordinates, so this one does bump.
      const pinned = pinToUserPosition(state);
      return { ...pinned, viewportNonce: state.viewportNonce + 1 };
    }

    case 'CLEARED': {
      return initialPickerState(false);
    }
  }
}
